import {
  Component,
  OnInit,
  Input,
  ViewChild,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { TranslateService } from '@ngx-translate/core';
import { DecimalPipe, UpperCasePipe } from '@angular/common';
import { OnyxEvent } from '../../models/event';
import { AdjustmentStore } from '../../service/adjustment.store';
import { EnvironmentConfiguration } from '../../../environment/services/environment.configuration';
import { ErrorHandlerService } from '../../../shared/utils/error-handler.service';
import { LogService } from '../../../shared/log/log.service';
import { Payment } from '../../models/payment';
import { AuthenticationStore } from '../../../permission/services/authentication.store';
import { VendorsTotalPaymentsSummary } from 'app/event/models/vendors-total-payments-summary';
import { ConfirmationModalComponent } from 'app/shared/confirmation-modal/confirmation-modal.component';
import { ConfirmationModalConfig } from 'app/shared/confirmation-modal/confirmation-modal-config';
import { Party } from 'app/event/models/party';
import { PaymentType } from 'app/event/models/payment-type';
import { FlashMessagesService } from 'app/shared/flash-messages/service/flash.messages.service';
import { PaymentStore } from 'app/event/service/payment.store';
import { map, mergeMap, take } from 'rxjs/operators';
import { EventStore } from 'app/event/service/event.store';
import { EventUtils } from 'app/event/utils/event-utils';
import { GroupPayConfigStore } from 'app/event/service/grouppay-config.store';
import { TaxDeterminationStore } from 'app/event/service/tax-determination.store';
import { OoivRulesStore } from 'app/event/service/ooiv-rules.store';
import { isNullOrUndefined } from 'util';
import { Unsubscriber } from './../../../shared/helper/unsubscriber';
import { PioneerValidators } from 'app/shared/utils/pioneer.validators';
import { BulkActionResponse } from '../../models/bulk-action-response';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { PaymentSummaryStore } from 'app/event/service/payment-summary/payment-summary.store';
import { PaymentSummary } from 'app/event/models/payment-summary/payment-summary';
import { TaxResponse } from 'app/event/models/TaxResponse';
import { List } from 'immutable';
import { of } from 'rxjs/observable/of';


@Component({
  selector: 'pioneer-vendors-total-add-payment',
  templateUrl: './vendors-total-add-payment.component.html',
  styleUrls: ['./vendors-total-add-payment.component.css']
})
export class VendorsTotalAddPaymentComponent extends Unsubscriber implements OnInit, OnChanges {

  @ViewChild(ConfirmationModalComponent)
  confirmationModal: ConfirmationModalComponent;
  confirmationModalConfig = new ConfirmationModalConfig();

  @Input()
  prepayment: Payment = null;

  finalPaymentHint: string;
  onyxEvent: OnyxEvent;
  applyOoiv = false;
  isNotPendingTaxEstimated = true;
  ooivRulesValidation = true;
  ooivEntityReady = true;

  private isNotMissingEmailContact = false;
  private atLeastOnevendorIsMissingEmailContact = false;
  private bulkActionInfo: string;

  get calculatedTotalPaid(): Observable<number> {
    return this.adjustmentStore.vendorsTotalPaymentsSummary
      .map((totals) => {
        return totals.totalPaid;
      });
  }

  get calculatedTotalRemaining(): Observable<number> {
    return this.adjustmentStore.vendorsTotalPaymentsSummary
      .map((totals) => {
        return totals.totalRemaining;
      });
  }

  get calculateTotalPayment(): Observable<number> {
    return this.adjustmentStore.vendorsTotalPaymentsSummary
      .map((totals) => {
        return totals.totalPayment;
      });
  }

  get totalVendorsWithRemainingPayment(): Observable<string> {
    return this.adjustmentStore.vendorsTotalPaymentsSummary
      .map((totals) => {
        return totals.totalVendorsWithRemainingPayment.toString();
      });
  }

  constructor(
    private translateService: TranslateService,
    private environment: EnvironmentConfiguration,
    private errorHandlerService: ErrorHandlerService,
    private logService: LogService,
    private authenticationStore: AuthenticationStore,
    private adjustmentStore: AdjustmentStore,
    private decimalPipe: DecimalPipe,
    private uppercasePipe: UpperCasePipe,
    private flashMessagesService: FlashMessagesService,
    private paymentStore: PaymentStore,
    private eventStore: EventStore,
    private taxDeterminationStore: TaxDeterminationStore,
    private groupPayConfigStore: GroupPayConfigStore,
    private ooivRulesStore: OoivRulesStore,
    private paymentSummaryStore: PaymentSummaryStore
  ) {
    super();
  }

  @Input('event')
  set event(event: OnyxEvent) {
    this.onyxEvent = event;
  }

  ngOnInit(): void {
    if (!this.onyxEvent) {
      this.errorHandlerService.throw(
        'Input property onyxEvent is null in AddPaymentComponent',
        this.translateService.instant('MESSAGES.ERROR.INPUT_PROPERTY_NULL', {
          property: 'onyxEvent',
          className: 'AddPaymentComponent'
        })
      );
    }

    this.retrieveFinalPaymentHint();

    this.groupPayConfigStore.isOoivEnabled(this.onyxEvent.eventAgency.code,
      this.onyxEvent.eventAgency.countryCode, this.onyxEvent.eventProperty.countryCode)
      .subscribe(ooivEnabled => {
        this.applyOoiv = ooivEnabled;
      });

    this.addSubscription(this.ooivRulesStore.getAnyOoivExceptionRule()
      .subscribe(rule => {
        if (rule) {
          this.ooivRulesValidation = rule.ooivExceptionsIsValid;
        }

        if (this.ooivRulesValidation) {
          this.ooivRulesStore.getAgencyOoivExceptionRule().subscribe(agencyRule => {
            if (agencyRule) {
              this.ooivEntityReady = agencyRule.ooivExceptionsIsValid;
            }
          });
        }
      }));

    this.addSubscription(this.taxDeterminationStore.getGlobalResponseTaxesList().subscribe((taxes) => {
      this.isNotPendingTaxEstimated = taxes.isValidTax;
    }));

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['event']) {
      return;
    }
  }

  private isValidPaymentDate(): boolean {
    if (!this.onyxEvent || !this.getEventEndDate()) {
      return false;
    }

    const firstPayDate = new Date(this.getEventEndDate());
    firstPayDate.setDate(firstPayDate.getDate() + this.environment.payments.paymentDelayDaysAfterEventEnd + 1);

    const firstPayDateUtcSeconds = firstPayDate.getTime();
    const currentUtcSeconds = new Date().getTime();
    const isValid = currentUtcSeconds >= firstPayDateUtcSeconds;

    this.logService.verbose(`allow: ${firstPayDateUtcSeconds}, current: ${currentUtcSeconds}, isValid: ${isValid}`);

    return isValid;
  }

  confirmBeforeAddPaymentHandler(value) {
    if (value) {
      this.savePayments();
    }
  }

  isNotMissingVendorEmailContact(): boolean {
    this.onyxEvent.eventVendors.forEach(vendor => {
      if (!this.isNotMissingEmailContact && !isNullOrUndefined(vendor.contact.email) && vendor.contact.email.trim().length > 0) {
        this.isNotMissingEmailContact = true;
      }
      if (!this.atLeastOnevendorIsMissingEmailContact && isNullOrUndefined(vendor.contact.email)) {
        this.atLeastOnevendorIsMissingEmailContact = true;
      } else {
        if (!this.atLeastOnevendorIsMissingEmailContact && vendor.contact.email.trim().length === 0) {
          this.atLeastOnevendorIsMissingEmailContact = true;
        }
      }
    });
    return this.isNotMissingEmailContact;
  }

  private isAtLeastOneInvalidNonSubscriberVendorEmailContact(): boolean {
    let atLeastOneVendorHasInvalidEmailContact = false;
    this.onyxEvent.eventVendors.forEach(vendor => {
      if (!vendor.subscriber && !PioneerValidators.isEmailFormatValid(vendor.contact.email)) {
        atLeastOneVendorHasInvalidEmailContact = true;
      }
    });
    return atLeastOneVendorHasInvalidEmailContact;
  }

  showRequestPaymentFromAllVendors() {
    return this.isMultiVendor() && !this.eventOnlyHasSubscribers();
  }

  confirmBeforeAuthorizingPayment(): void {
    // set up the title of the confirmation modal
    this.addSubscription(this.adjustmentStore.vendorsTotalPaymentsSummary.map((totalSummary: VendorsTotalPaymentsSummary) => {
      const label = this.isMultiVendor()
        ? 'EVENT.COMMISSION.REQUEST_PAYMENT_VENDOR_CONFIRMATION_TITLE' : 'EVENT.COMMISSION.AUTHORIZE_PAYMENT_CONFIRMATION_TITLE';
      const confirmLabel = this.isMultiVendor()
        ? 'EVENT.COMMISSION.CONFIRMATION_REQUEST' : 'EVENT.COMMISSION.CONFIRMATION_CONFIRM';
      const params = {
        vendors: totalSummary.totalVendorsWithRemainingPayment,
        paymentAmount: this.decimalPipe.transform(totalSummary.totalRemaining, '1.2-2'),
        currency: this.uppercasePipe.transform(this.onyxEvent.currency)
      };
      const title = this.translateService.instant(label, params);
      const confirmTitle = this.translateService.instant(confirmLabel);
      this.confirmationModalConfig.title = title;
      this.confirmationModalConfig.confirm = confirmTitle;
    }).subscribe());

    this.confirmationModal.open();
  }

  private retrieveFinalPaymentHint() {
    this.isNotMissingVendorEmailContact();

    if (this.atLeastOnevendorIsMissingEmailContact) {
      this.finalPaymentHint = this.translateService.instant('MESSAGES.ERROR.MULTIVENDOR_EMAIL_MISSING');
    } else if (this.isAtLeastOneInvalidNonSubscriberVendorEmailContact()) {
      this.finalPaymentHint = this.translateService.instant('MESSAGES.ERROR.MULTIVENDOR_EMAIL_INVALID');
    } else {
      this.finalPaymentHint = this.translateService.instant('EVENT.COMMISSION.PAYMENTS_WILL_BE_PROCESSED');
    }
  }

  private savePayments() {
    forkJoin(
      this.onyxEvent.eventVendors.map(vendor => this.getVendorPayment(vendor))
    )
    .pipe(
      map((payments: Payment[]) => payments.filter(payment => payment !== null))
    )
    .subscribe(validPayments => {
      this.processPayments(validPayments);
    });
  }

  private getVendorPayment(vendor: Party): Observable<Payment | null> {
    if (!this.isVendorEligible(vendor)) {
      return of(null);
    }

    return this.paymentSummaryStore.getPaymentSummary(vendor.eventPartyId).pipe(
      take(1),
      mergeMap((paymentSummary: PaymentSummary) => {
        if (!paymentSummary.commissionAmount || paymentSummary.commissionAmount <= 0) {
          return of(null);
        }

        return this.canAddPayment(vendor.eventPartyId).pipe(
          take(1),
          mergeMap((canAddPayment: boolean) => {
            if (!canAddPayment) {
              return of(null);
            }

            return this.groupPayConfigStore.isOoivEnabled(
              this.onyxEvent.eventAgency.code,
              this.onyxEvent.eventAgency.countryCode,
              vendor.countryCode
            ).pipe(
              take(1),
              mergeMap((ooivEnabled: boolean) =>
                this.getVendorTaxDetail(ooivEnabled, vendor).pipe(
                  map((taxResponseList: List<TaxResponse> | null) => {
                    const totalTaxes = ooivEnabled ? paymentSummary.estimatedTax : 0;
                    return this.buildPayment(
                      paymentSummary.commissionAmount,
                      totalTaxes,
                      taxResponseList,
                      vendor
                    );
                  })
                )
              )
            );
          })
        );
      })
    );
  }

  private isVendorEligible(vendor: Party): boolean {
    return vendor &&
      !vendor.subscriber &&
      vendor.contact &&
      vendor.contact.email &&
      vendor.contact.email.length > 1;
  }

  private buildPayment(
    commissionAmount: number,
    totalVendorTaxes: number,
    taxResponseList: List<TaxResponse> | null,
    vendor: Party
  ): Payment {
    return new Payment({
      commissionAmount: commissionAmount,
      description: '',
      commissionCurrency: this.onyxEvent.currency,
      eventId: this.onyxEvent.eventId,
      type: PaymentType.FINAL_PAYMENT,
      eventPartyId: vendor.eventPartyId,
      totalTaxAmount: totalVendorTaxes,
      eventPaymentTaxDetail: taxResponseList
    });
  }

  private getVendorTaxDetail(
    ooivEnabled: boolean,
    vendor: Party,
  ): Observable<List<TaxResponse> | null> {
    return ooivEnabled
      ? this.taxDeterminationStore.vendorTaxRates(vendor.eventPartyId, true).pipe(take(1))
      : of(null);
  }


  private processPayments(payments: Payment[]) {
    let failedPaymentCounter = 0;
    this.bulkActionInfo = this.translateService.instant('EVENTS.BULK_ACTION.PROCESSING', { paymentCount: payments.length });

    this.paymentStore.bulkActionPayments(payments)
    .subscribe((res: BulkActionResponse[]) => {
      const failedPayments = res.filter(r => r.error);
      failedPaymentCounter = failedPayments.length;
        // Retrieve failures
        if (failedPaymentCounter > 0) {
          this.bulkActionInfo = this.translateService.instant(
            'EVENTS.BULK_ACTION.FAILURES',
            { failedPaymentCount: failedPayments.length, paymentCount: payments.length });
          failedPayments.forEach(r => {
              let vendor = this.onyxEvent.eventVendors.find(v => v.eventPartyId === r.eventPartyId);
              this.bulkActionInfo += this.translateService.instant(
                'EVENTS.BULK_ACTION.PAYMENT_ERROR',
                { eventReferenceNumber: vendor.eventReferenceNumber, vendorName: vendor.name, error: r.error });
          });

          this.logService.debug(this.bulkActionInfo);
        }
        this.refreshSelectedEvent(true);
        },
      (err) => {
        this.logService.error(`Error processing Bulk Payments: ${err}`);
        this.flashMessagesService.error('MESSAGES.ERROR.ADD_PAYMENT', `Error processing Payment: ${err}`);
        },
      () => {
        this.logService.debug('Bulk Payments completed');
        }
    );
  }

  private refreshSelectedEvent(successBanner: boolean) {
    // firing off both calls because both have impacts elsewhere:
    //   CommissionComponent and PaymentEquationComponent
    const eventId = this.onyxEvent.eventId;
    forkJoin([
      this.paymentStore.loadPayments(eventId).take(1),
      this.paymentStore.loadLatestFinalPayment(eventId).take(1),
      this.paymentStore.loadFinalPaymentStatusList(eventId).take(1),
      this.eventStore.getEvent(eventId).take(1),
      this.adjustmentStore.loadVendorsTotalPaymentsSummary(eventId).take(1)
    ]).subscribe(() => {
      if (successBanner) {
        this.flashMessagesService.success('EVENT.COMMISSION.PAYMENTS_SAVED');
      }
    });
  }

  private canAddPayment(eventPartyId: number): Observable<boolean> {
    return this.paymentStore.isCurrentVendorPaymentValid(eventPartyId).map(currentPaymentValid => {
      return !currentPaymentValid &&
        this.isValidPaymentDate();
    });
  }

  private isMultiVendor() {
    return this.authenticationStore.hasPermission('MultiVendor');
  }

  private eventOnlyHasSubscribers(): boolean {
    return this.onyxEvent.eventVendors
      && this.onyxEvent.eventVendors.filter(vendor => !vendor.subscriber).length === 0;
  }

  private getEventEndDate() {
    return EventUtils.getEventEndDate(this.onyxEvent, this.authenticationStore.isAgencyUser());
  }

  disableConfirmBeforeAuthorizingPayment(): Observable<boolean> {
    if (this.isMissingVendorEmailContact()) {
      return Observable.of(true);
    } else if (this.isAtLeastOneInvalidNonSubscriberVendorEmailContact()) {
      return Observable.of(true);
    } else if (this.applyOoiv && !this.ooivRulesValidation) {
      return Observable.of(true);
    } else if (this.applyOoiv && !this.ooivEntityReady) {
      return Observable.of(true);
    } else if (this.applyOoiv && this.isPendingTaxEstimated()) {
      return Observable.of(true);
    } else {
      return this.adjustmentStore.vendorsTotalPaymentsSummary
        .map((vendorsTotalPaymentsSummary) => {
          return !vendorsTotalPaymentsSummary.totalVendorsWithRemainingPayment
            || vendorsTotalPaymentsSummary.totalVendorsWithRemainingPayment === 0;
        });
    }
  }

  private isPendingTaxEstimated(): boolean {
    return !this.isNotPendingTaxEstimated;
  }

  private isMissingVendorEmailContact(): boolean {
    return !this.isNotMissingVendorEmailContact();
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/payment/vendors-total-add-payment/vendors-total-add-payment.component.ts